/**
 * Impresión térmica del comprobante fiscal ARCA (Factura/NC A·B·C).
 * El documento incluye CAE, vencimiento y QR AFIP — reemplaza al ticket
 * interno cuando la venta sale en modo fiscal.
 */
import { buildFiscalComprobante, type FiscalReceiptPayload } from '@vuelto/print';
import { Platform } from 'react-native';
import { showError, showInfo } from '../../shared/ui/appToast';
import { printThermalDocumentJob } from './printThermalDocument';
import { getBranchReceiptPrintConfig } from '../infra/printConfigRepo';
import {
  AFIP_CBTE,
  spanishInvoiceTypeLabel,
  spanishIvaReceptorLabel,
  type TaxRegime,
} from '../../fiscal/domain-api';
import { withFiscalTipLine } from '../../fiscal/domain-api';
import { buildAfipQrUrl, encodeBase64Utf8 } from '../../fiscal/infra-api';

/** Emisor del comprobante — perfil fiscal de la org (o `emitter` de la respuesta de emisión). */
export type FiscalReceiptEmitterInput = {
  businessName?: string | null;
  cuit?: string | null;
  taxRegime?: TaxRegime | string | null;
  domicilioComercial?: string | null;
  iibb?: string | null;
  inicioActividades?: string | null;
};

export type FiscalReceiptItemInput = {
  label: string;
  quantity: number;
  price: number;
};

/** Datos del comprobante emitido (factura o NC) + contexto de la venta. */
export type FiscalReceiptPrintInput = {
  invoiceType?: number | null;
  pointOfSale?: number | null;
  invoiceNumber?: number | null;
  cae: string;
  caeExpiration?: string | null;
  issuedAt?: string | null;
  qrUrl?: string | null;
  /** JSON del QR AFIP (pos.invoices.qr_payload) — regenera la URL si falta `qrUrl`. */
  qrPayload?: string | null;
  buyerName?: string | null;
  buyerCuit?: string | null;
  buyerTaxCondition?: number | null;
  items: FiscalReceiptItemInput[];
  /** Propina cobrada en el POS — se imprime como línea aparte (ImpTotal AFIP ya la incluye). */
  tipAmount?: number;
  total: number;
  /** Factura A: neto gravado + IVA discriminado. */
  netAmount?: number | null;
  ivaAmount?: number | null;
  saleRef?: string | null;
  diningTableName?: string | null;
  /** 'ORIGINAL' al emitir en el cobro; 'DUPLICADO' al reimprimir desde Ventas. */
  copyLabel?: string | null;
  /** NC: referencia al comprobante anulado ('Anula FACTURA B 00003-00000042'). */
  relatedDocLabel?: string | null;
  emitter: FiscalReceiptEmitterInput;
  environment?: string | null;
  /** Ancho de papel de la sucursal (lo resuelve printFiscalComprobante si falta). */
  widthMm?: 58 | 72 | 80;
};

const CREDIT_NOTE_TYPES = new Set<number>([
  AFIP_CBTE.NOTA_CREDITO_A,
  AFIP_CBTE.NOTA_CREDITO_B,
  AFIP_CBTE.NOTA_CREDITO_C,
]);

function emitterTaxConditionLabel(taxRegime?: string | null): string {
  return taxRegime === 'responsable_inscripto' ? 'Responsable Inscripto' : 'Monotributo';
}

/** PNG (web) / SVG base64 (nativo) del QR AFIP para la vista previa HTML. */
async function buildFiscalQrImageDataUrl(qrUrl: string | null): Promise<string | null> {
  if (!qrUrl) return null;
  try {
    const QRCode = await import('qrcode');
    if (Platform.OS === 'web') {
      return await QRCode.toDataURL(qrUrl, { width: 240, margin: 1 });
    }
    const svg = await QRCode.toString(qrUrl, { type: 'svg', width: 240, margin: 1 });
    return `data:image/svg+xml;base64,${encodeBase64Utf8(svg)}`;
  } catch (err) {
    console.warn('[printing] fiscal QR image failed', err);
    return null;
  }
}

/** Arma el payload del paquete @vuelto/print (async por el QR de la vista previa). */
export async function buildFiscalReceiptPayload(
  input: FiscalReceiptPrintInput
): Promise<FiscalReceiptPayload | null> {
  if (!input.cae?.trim()) return null;
  if (input.pointOfSale == null || input.invoiceNumber == null) return null;
  const qrUrl = buildAfipQrUrl(input.qrPayload ?? null, input.qrUrl ?? null, {
    buyerCuit: input.buyerCuit,
    cae: input.cae,
    issuedAt: input.issuedAt,
  });
  return {
    emitterBusinessName: input.emitter.businessName?.trim() ?? '',
    emitterCuit: input.emitter.cuit ?? '',
    emitterTaxConditionLabel: emitterTaxConditionLabel(input.emitter.taxRegime),
    emitterAddress: input.emitter.domicilioComercial,
    emitterIibb: input.emitter.iibb,
    emitterActivityStart: input.emitter.inicioActividades,
    documentTypeLabel: spanishInvoiceTypeLabel(input.invoiceType).toUpperCase(),
    isCreditNote: input.invoiceType != null && CREDIT_NOTE_TYPES.has(input.invoiceType),
    pointOfSale: input.pointOfSale,
    invoiceNumber: input.invoiceNumber,
    issuedAt: input.issuedAt ?? new Date().toISOString(),
    buyerName: input.buyerName,
    buyerCuit: input.buyerCuit,
    buyerTaxConditionLabel: spanishIvaReceptorLabel(input.buyerTaxCondition),
    lines: withFiscalTipLine(input.items, input.tipAmount ?? 0).map((item) => ({
      name: item.label,
      qty: item.quantity,
      unitPrice: item.price,
      lineTotal: item.price * item.quantity,
    })),
    netAmount: input.netAmount,
    ivaAmount: input.ivaAmount,
    total: input.total,
    cae: input.cae.trim(),
    caeExpiration: input.caeExpiration,
    qrUrl,
    qrImageDataUrl: await buildFiscalQrImageDataUrl(qrUrl),
    saleRef: input.saleRef,
    diningTableName: input.diningTableName,
    copyLabel: input.copyLabel,
    relatedDocLabel: input.relatedDocLabel,
    widthMm: input.widthMm,
    environment:
      input.environment === 'produccion'
        ? 'produccion'
        : input.environment === 'homologacion'
          ? 'homologacion'
          : null,
  };
}

/**
 * Imprime el comprobante fiscal por la térmica configurada (agente ESC/POS)
 * o vista previa browser. `blockWhenModeOff`: si la sucursal tiene la
 * impresión apagada devuelve false sin imprimir — el PDF sigue disponible.
 */
export async function printFiscalComprobante(args: {
  branchId: string;
  input: FiscalReceiptPrintInput;
}): Promise<boolean> {
  const widthMm =
    args.input.widthMm ?? (await getBranchReceiptPrintConfig(args.branchId)).widthMm;
  const payload = await buildFiscalReceiptPayload({ ...args.input, widthMm });
  if (!payload) {
    showError('No se pudo imprimir', 'El comprobante no tiene CAE o numeración.');
    return false;
  }
  const result = await printThermalDocumentJob(args.branchId, buildFiscalComprobante(payload), {
    blockWhenModeOff: true,
  });
  if (!result.ok) {
    showError('No se pudo imprimir', result.error);
    return false;
  }
  if (result.warning) showInfo('Impresión', result.warning);
  return true;
}
